import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AmountCard, EmptyState, ExplainerBanner, FlowLayout, Sheet, Skeleton } from '@/ds'
import { dayMonth, dayMonthLong, formatMoney, formatMoneyShort } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { fianzaService } from '@/services/fianza'
import { pagosService } from '@/services/pagos'
import { money } from '@/services/types'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { CardChoice, choiceErrors, chosenLast4, initialChoice, toCardInput } from '../Pagos/CardChoice'
import type { CardChoiceValue } from '../Pagos/CardChoice'
import { PAY_ROUTES, usePagosStay } from '../Pagos/shared'
import { RETURN_PARAM, useChangeExit } from '../Reserva/shared'
import { usePaymentStart } from '../Pagos/usePaymentStart'
import { bankDelayText, explainerFor, kindText, knownKind, pendingDepositDescription } from './copy'

/**
 * 8.3 · Reservar la fianza (FlujoFianza.dc.html, Pantallas del kit §Kf/§Kg). Tres pasos: 1 la tarjeta
 * (interpretación: el bundle solo dibuja el paso 2), 2 importe y hoja de confirmación (bundle), 3 la
 * autorización del banco y "Fianza reservada" (pantalla de autorización). Cobro real: E3 con `paidTypeBailId`.
 */
export function ReservarFianza() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const stay = usePagosStay()
  const { go, search } = useAppNavigate()
  const [deposit, reloadDeposit] = useAsync(`reservar-fianza:${stay.guestCode}:${apiLang}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const [summary, reloadSummary] = useAsync(`reservar-pagos:${stay.guestCode}:${apiLang}`, (signal) => pagosService.getPaymentSummary(stay, apiLang, signal))
  const [step, setStep] = useState<1 | 2>(1)
  const [choice, setChoice] = useState<CardChoiceValue | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const { start, starting, error } = usePaymentStart(stay.guestCode)

  const d = deposit.kind === 'ready' ? deposit.value : null
  const s = summary.kind === 'ready' ? summary.value : null
  const value = choice ?? (s ? initialChoice(s) : null)
  const loading = deposit.kind === 'loading' || summary.kind === 'loading'
  const failed = deposit.kind === 'error' ? deposit : summary.kind === 'error' ? summary : null
  const canReserve = d !== null && d.status === 'pending' && s !== null && value !== null

  const exit = useChangeExit()
  const hasOrigin = new URLSearchParams(search).has(RETURN_PARAM)
  const back = () => (step === 2 ? setStep(1) : hasOrigin ? exit.exit() : go(PAY_ROUTES.deposit))
  const next = () => {
    if (!s || !value) return
    setShowErrors(true)
    if (Object.keys(choiceErrors(s, value)).length === 0) setStep(2)
  }

  const amount = d ? formatMoney(d.amount, lang) : ''
  const last4 = s && value ? chosenLast4(s, value) : null
  const releaseDate = d?.holdUntil ?? null

  const reserve = () => {
    if (!d || !s || !value) return
    void start({ kind: 'deposit', amount: d.amount, returnTo: PAY_ROUTES.deposit, last4 }, () => fianzaService.reserveDeposit(stay, d.amount, toCardInput(s, value), apiLang)).then(() => setConfirm(false))
  }

  const sheetText = [
    last4 ? t('v2.fianza.reservar.hoja.tarjeta', { last4 }) : t('v2.fianza.reservar.hoja.tuTarjeta'),
    releaseDate ? t('v2.fianza.reservar.hoja.libera', { date: dayMonthLong(releaseDate, lang) }) : t('v2.fianza.reservar.hoja.liberaSinFecha'),
    d ? (kindText(d.kind, 'hoja', t) ?? '') : '',
  ]
    .join(' ')
    .trim()

  return (
    <FlowLayout
      progress={{ step, total: 3, backLabel: step === 2 ? t('v2.fianza.reservar.atrasTarjeta') : t('v2.fianza.reservar.atras'), onBack: back, saved: false }}
      title={t('v2.fianza.reservar.titulo')}
      intro={pendingDepositDescription(t)}
      primary={canReserve && step === 1 ? { label: t('v2.fianza.reservar.continuar'), onClick: next } : undefined}
    >
      {loading && (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={176} />
          <Skeleton height={120} />
        </div>
      )}
      {failed && failed.kind === 'error' && (
        <SectionError
          code={failed.code}
          onRetry={() => {
            reloadDeposit()
            reloadSummary()
          }}
        />
      )}
      {!loading && !failed && !canReserve && (
        <EmptyState icon="check-circle" title={d ? t('v2.fianza.reservar.yaReservada') : t('v2.fianza.sinFianza')} headingLevel={2} actionLabel={t('v2.fianza.reservar.verFianza')} onAction={() => go(PAY_ROUTES.deposit, { replace: true })} />
      )}
      {canReserve && step === 1 && (
        <div className="pagos__stack">
          <p>{t('v2.fianza.reservar.paso1')}</p>
          <CardChoice summary={s} value={value} onChange={setChoice} showErrors={showErrors} />
        </div>
      )}
      {canReserve && step === 2 && (
        <div className="pagos__stack">
          <AmountCard
            total={amount}
            totalLabel={t('v2.fianza.reservar.importe')}
            lines={[
              { label: t('v2.fianza.reservar.fianzaDe', { name: stay.unit.name || stay.property.name }), amount },
              // Qué se cobra ahora solo se dice cuando el backend da el tipo (D7).
              ...(knownKind(d.kind) ? [{ label: t('v2.fianza.reservar.seCobraAhora'), amount: formatMoney(knownKind(d.kind) === 'charge' ? d.amount : money(0, d.amount.currency), lang) }] : []),
            ]}
            totalRowLabel={releaseDate ? t('v2.fianza.reservar.retenidoHasta', { date: dayMonth(releaseDate, lang) }) : t('v2.fianza.reservar.retenidoSinFecha')}
            note={kindText(d.kind, 'nota', t) ?? undefined}
            buttonLabel={t('v2.fianza.reservar.boton', { amount })}
            onPay={() => setConfirm(true)}
            loading={starting}
          >
            {error && (
              <p className="pagos__error" role="alert">
                {error}
              </p>
            )}
          </AmountCard>
          {knownKind(d.kind) && (
            <ExplainerBanner title={explainerFor(d, t)?.title}>{[kindText(d.kind, 'reservarExplicador', t, { amount: formatMoneyShort(d.amount, lang) }), bankDelayText(d, t)].filter(Boolean).join(' ')}</ExplainerBanner>
          )}
        </div>
      )}
      <Sheet open={confirm} title={t('v2.fianza.reservar.boton', { amount })} confirmLabel={t('v2.fianza.reservar.boton', { amount })} onConfirm={reserve} onCancel={() => setConfirm(false)} loading={starting}>
        {sheetText}
      </Sheet>
    </FlowLayout>
  )
}
